import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Package, Pencil, X, Eraser } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { StatusPill } from "@/components/Pills";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

export default function MyOrders() {
  const nav = useNavigate();
  const { t, displayName } = useLang();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { load(); }, []);
  const load = () => {
    setLoading(true);
    api.get("/orders").then(r => setOrders(r.data)).finally(() => setLoading(false));
  };

  const repeat = (o) => nav("/shop/place", { state: { repeatItems: o.items } });
  const editOrder = (o) => nav("/shop/place", { state: { editOrder: o } });

  const confirmCancel = async () => {
    if (!cancelling) return;
    setBusy(true);
    try {
      await api.post(`/orders/${cancelling.id}/cancel`);
      toast.success(t("order_cancelled"));
      setCancelling(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const confirmClearHistory = async () => {
    setBusy(true);
    try {
      await api.post(`/shopkeeper/clear-history`);
      toast.success(t("history_cleared"));
      setClearingHistory(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <TopBar title={t("my_orders")} onBack={() => nav("/shop")} />
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        {loading ? (
          <div className="text-center text-[#86868B] py-12">{t("loading")}</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 mx-auto text-[#86868B] mb-3" />
            <div className="text-[#86868B]">{t("no_orders")}</div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-end mb-1">
              <button
                data-testid="clear-history-btn"
                onClick={() => setClearingHistory(true)}
                className="flex items-center gap-2 text-[#86868B] hover:text-red-600 hover:bg-red-50 rounded-xl px-3 py-2 text-sm font-bold"
              >
                <Eraser className="w-4 h-4" /> {t("clear_history")}
              </button>
            </div>
            {orders.map(o => {
              const canModify = o.status === "pending";
              return (
                <div key={o.id} className="bg-white rounded-2xl p-5 soft-shadow fade-up" data-testid={`order-${o.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-xs text-[#86868B] uppercase tracking-wider">{t("order_date")}</div>
                      <div className="heading-font font-bold text-lg text-[#1D1D1F]">{new Date(o.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <StatusPill status={o.status} t={t} />
                      {o.priority && <span className="pill bg-red-50 text-red-700">⚡ {t("high_priority")}</span>}
                    </div>
                  </div>
                  <div className="text-sm text-[#86868B] mb-3">
                    {o.items.length} {t("items")} • {o.total_units} {t("units")}
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {o.items.slice(0, 4).map((it, i) => (
                      <span key={i} className="pill bg-gray-100 text-gray-700">
                        {displayName(it)} ×{it.quantity}
                      </span>
                    ))}
                    {o.items.length > 4 && <span className="pill bg-gray-100 text-gray-700">+{o.items.length - 4}</span>}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {canModify && (
                      <>
                        <button
                          data-testid={`edit-${o.id}`}
                          onClick={() => editOrder(o)}
                          className="flex items-center gap-2 bg-[#FF9500]/10 hover:bg-[#FF9500]/20 text-[#B96E00] rounded-xl px-3 py-2 text-sm font-bold"
                        >
                          <Pencil className="w-4 h-4" /> {t("modify_order")}
                        </button>
                        <button
                          data-testid={`cancel-${o.id}`}
                          onClick={() => setCancelling(o)}
                          className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl px-3 py-2 text-sm font-bold"
                        >
                          <X className="w-4 h-4" /> {t("cancel_order")}
                        </button>
                      </>
                    )}
                    <button
                      data-testid={`repeat-${o.id}`}
                      onClick={() => repeat(o)}
                      className="flex items-center gap-2 bg-[#0055FF]/10 hover:bg-[#0055FF]/20 text-[#0055FF] rounded-xl px-3 py-2 text-sm font-bold"
                    >
                      <RefreshCw className="w-4 h-4" />
                      {t("repeat_order")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {clearingHistory && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => !busy && setClearingHistory(false)}>
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-sm w-full soft-shadow" onClick={e => e.stopPropagation()} data-testid="clear-history-modal">
            <div className="w-14 h-14 mx-auto rounded-full bg-orange-50 grid place-items-center mb-3">
              <Eraser className="w-7 h-7 text-orange-600" />
            </div>
            <div className="heading-font text-xl font-bold text-[#1D1D1F] text-center mb-5">{t("clear_history_confirm")}</div>
            <div className="flex gap-3">
              <button onClick={() => setClearingHistory(false)} disabled={busy} data-testid="clear-keep-btn" className="flex-1 bg-gray-100 hover:bg-gray-200 text-[#1D1D1F] rounded-2xl py-3 font-bold">{t("no_keep")}</button>
              <button onClick={confirmClearHistory} disabled={busy} data-testid="clear-confirm-btn" className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded-2xl py-3 font-bold">{t("yes_delete")}</button>
            </div>
          </div>
        </div>
      )}


      {cancelling && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => !busy && setCancelling(null)}>
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-sm w-full soft-shadow" onClick={e => e.stopPropagation()} data-testid="cancel-confirm-modal">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 grid place-items-center mb-3">
              <X className="w-7 h-7 text-red-600" />
            </div>
            <div className="heading-font text-xl font-bold text-[#1D1D1F] text-center mb-2">{t("cancel_order_confirm")}</div>
            <div className="text-sm text-[#86868B] text-center mb-5">
              {cancelling.items.length} {t("items")} • {cancelling.total_units} {t("units")}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setCancelling(null)}
                disabled={busy}
                data-testid="cancel-keep-btn"
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-[#1D1D1F] rounded-2xl py-3 font-bold"
              >
                {t("no_keep")}
              </button>
              <button
                onClick={confirmCancel}
                disabled={busy}
                data-testid="cancel-confirm-btn"
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-2xl py-3 font-bold"
              >
                {t("yes_cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
